import React from "react";

import { useTranslation } from "react-i18next";
import { Button, Divider, Form, Input, Modal, Segmented, Select, Space, Spin, Typography } from "antd";

import { useDispatch, useSelector } from "react-redux";

import slice from "./reducer";
import saga from "./saga";

import {
  makeSelectAdminOfficeType,
  makeSelectDealId,
  makeSelectDealNotifications,
  makeSelectLoading,
  makeSelectOpen,
  selectQuestionContext,
  selectQuestionContextLoading,
  selectQuestionContextError,
  selectCreatedQuestionRequestId,
} from "./selectors";

import useInjectSaga from "../../utils/useInjectSaga";
import { makeSelectUserInfo } from "../../containers/App/selectors";

import DealNotificationsList from "../../components/DealNotificationsList";
import { SendOutlined } from "@ant-design/icons";
import { createUserSelectOption, filterUserSelectOption } from "../../utils/UserSelectUtils";
import { DealNotificationUtils } from "../../viewModels/Notification/DealNotificationModel";

type SegmentType = "ALL" | "ACTION_REQUIRED";

type CreateDealNotificationFormValues = {
  recipientIds?: Array<number>;
  request: string;
};

const DealNotificationModal = React.memo(() => {
  const { t } = useTranslation("translations");

  const userInfo = useSelector(makeSelectUserInfo());
  const context = useSelector(selectQuestionContext);
  const contextLoading = useSelector(selectQuestionContextLoading);
  const contextError = useSelector(selectQuestionContextError);
  const createdRequestId = useSelector(selectCreatedQuestionRequestId);
  const submission = React.useRef<{ signature: string; id: string }>();

  const [segment, setSegment] = React.useState<SegmentType>("ACTION_REQUIRED");
  const [form] = Form.useForm<CreateDealNotificationFormValues>();
  const request = Form.useWatch("request", form);

  useInjectSaga("dealNotificationModal", saga);

  const dispatch = useDispatch();

  const open = useSelector(makeSelectOpen);
  const loading = useSelector(makeSelectLoading);
  const dealNotifications = useSelector(makeSelectDealNotifications);
  const dealId = useSelector(makeSelectDealId);
  const adminOfficeType = useSelector(makeSelectAdminOfficeType);

  const recipientUsers = React.useMemo(() => {
    return (context?.employees || []).filter((x) => x.id !== context?.managerId && !x.locked);
  }, [context]);

  const recipientOptions = React.useMemo(() => recipientUsers.map((x) => createUserSelectOption(x)), [recipientUsers]);

  const actionNeededDealNotifications = dealNotifications.filter((x) => DealNotificationUtils.isActionRequiredForUser(x, userInfo.id));

  const handleClose = React.useCallback(() => {
    dispatch(slice.actions.closeModal());
  }, [dispatch]);

  const handleCreateDealNotification = React.useCallback(
    (values: CreateDealNotificationFormValues) => {
      if (!context) return;
      const recipientIds = Array.from(new Set([context.managerId, ...(values.recipientIds || [])]));
      const signature = JSON.stringify({ dealId, request: values.request, recipientIds, officeType: context.officeType });
      if (submission.current?.signature !== signature) submission.current = { signature, id: window.crypto.randomUUID() };
      dispatch(
        slice.actions.createDealNotification({
          requestId: submission.current.id,
          officeType: context.officeType,
          dealId,
          request: values.request,
          recipientIds,
        }),
      );
    },
    [context, dealId, dispatch],
  );

  const loadContext = React.useCallback(() => {
    dispatch(slice.actions.loadContext({ dealId, officeType: adminOfficeType }));
  }, [adminOfficeType, dealId, dispatch]);

  React.useEffect(() => {
    if (open && (userInfo.role.isBoss || userInfo.role.userType === "ADMINISTRATOR")) loadContext();
  }, [loadContext, open, userInfo.role.isBoss, userInfo.role.userType]);

  React.useEffect(() => {
    if (createdRequestId && createdRequestId === submission.current?.id) {
      form.resetFields();
      submission.current = undefined;
    }
  }, [createdRequestId, form]);

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      submission.current = undefined;
    }
  }, [form, open]);

  return (
    <Modal open={open} onCancel={handleClose} footer={null} destroyOnHidden title={t("deal_notifications")} width={1000}>
      <Spin spinning={loading}>
        <Space orientation="vertical" style={{ width: "100%" }} separator={<Divider size="small" />}>
          {(userInfo.role.isBoss || userInfo.role.userType === "ADMINISTRATOR") && (
            <div>
              <Typography.Title level={5}>{t("ask_request_to_manager")}</Typography.Title>
              {contextError && (
                <Space>
                  <Typography.Text type="danger">{t("error")}</Typography.Text>
                  <Button onClick={loadContext}>{t("update")}</Button>
                </Space>
              )}
              <Form form={form} layout="vertical" onFinish={handleCreateDealNotification}>
                <Form.Item name="recipientIds" label={t("additional_recipients")}>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch={{ filterOption: filterUserSelectOption }}
                    maxTagCount="responsive"
                    options={recipientOptions}
                    loading={contextLoading}
                    disabled={!context || contextLoading}
                  />
                </Form.Item>
                <Form.Item name="request" label={t("request")} rules={[{ required: true, message: t("enter_request") }]}>
                  <Input.TextArea autoSize />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    htmlType="submit"
                    type="primary"
                    icon={<SendOutlined />}
                    disabled={!request?.trim() || !context || contextLoading}
                  >
                    {t("send")}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          )}

          <Space orientation="vertical" style={{ width: "100%" }} size={16}>
            <Segmented<SegmentType>
              value={segment}
              options={[
                { label: t("all"), value: "ALL" },
                { label: t("action_required"), value: "ACTION_REQUIRED" },
              ]}
              onChange={setSegment}
            />
            <DealNotificationsList
              notifications={segment === "ALL" ? dealNotifications : actionNeededDealNotifications}
              adminOfficeType={adminOfficeType}
            />
          </Space>
        </Space>
      </Spin>
    </Modal>
  );
});

export default DealNotificationModal;
